import {useApolloClient} from '@apollo/client';
import {useObservabilityConfig, useProject} from '@sail/observability';
import {useMemoizedValue} from '@sail/react';
import * as React from 'react';
import PrefetchEnvContext from 'src/internal/prefetching/PrefetchEnvContext';
import PruningPrefetchCacheContext from 'src/internal/prefetching/PruningPrefetchCacheContext';
import useDefaultFetchPolicy from 'src/internal/prefetching/useDefaultFetchPolicy';
import useInteroperabilityLayer from 'src/internal/revamp/config/useInteroperabilityLayer';
import useObservabilityFns from 'src/internal/shared/observability/useObservabilityFns';
import useDataConfig from 'src/public/useDataConfig';

import type {
  InternalOperation,
  UseQueryOptionsWithoutVariables,
} from 'src/internal/apollo/types';
import type {
  GraphQlDocument,
  PermissionsOfDoc,
} from 'src/internal/shared/graphql/types/document';

export type InternalOperationContext<
  TDocumentNode extends GraphQlDocument<any, any, any>,
> = ReturnType<InternalOperation<TDocumentNode>['getContext']>;

export default function useGetApolloContext<
  TDocumentNode extends GraphQlDocument<any, any, any>,
>(
  options: Pick<
    UseQueryOptionsWithoutVariables<TDocumentNode>,
    'expectedErrors' | 'keepAlive' | 'mutedErrors' | 'component' | 'fetchPolicy'
  > &
    Partial<PermissionsOfDoc<TDocumentNode>>,
): InternalOperationContext<PermissionsOfDoc<TDocumentNode>> {
  const dataConfig = useDataConfig();
  const defaultFetchPolicy = useDefaultFetchPolicy();
  const fetchPolicy = options.fetchPolicy || defaultFetchPolicy;
  const memoizedExpectedErrors = useMemoizedValue(options.expectedErrors);
  const memoizedMutedErrors = useMemoizedValue(options.mutedErrors);
  const pruningSignal = React.useContext(PruningPrefetchCacheContext);
  const token = options.token;
  const {isPrefetchMode} = React.useContext(PrefetchEnvContext);
  const client = useApolloClient();

  const observabilityFns = useObservabilityFns();
  const queryProject = useProject();
  const service = useObservabilityConfig()?.service;
  const boundaryViewToken = useObservabilityConfig()?.boundaryViewToken;
  const {expectedApiErrors, mutedApiErrors} = dataConfig.observability;
  const computedExpectedErrors = React.useMemo(() => {
    return expectedApiErrors.concat(memoizedExpectedErrors ?? []);
  }, [memoizedExpectedErrors, expectedApiErrors]);

  const computedMutedErrors = React.useMemo(() => {
    return mutedApiErrors.concat(memoizedMutedErrors ?? []);
  }, [memoizedMutedErrors, mutedApiErrors]);

  const fetch = dataConfig.fetch;

  const interoperabilityLayer = useInteroperabilityLayer();

  return React.useMemo((): InternalOperationContext<
    PermissionsOfDoc<TDocumentNode>
  > => {
    const fetchOptions = {
      keepalive: !!options.keepAlive,
    };

    const context: InternalOperationContext<PermissionsOfDoc<TDocumentNode>> = {
      client,
      component: options.component,
      expectedErrors: computedExpectedErrors,
      fetch,
      fetchOptions,
      fetchPolicy,
      isPrefetchMode,
      prefetchCachePruningSignal: pruningSignal,
      mutedErrors: computedMutedErrors,
      observabilityFns,
      queryProject,
      interoperabilityLayer,
      service,
      boundaryViewToken,
      token,
    };

    return context;
  }, [
    client,
    computedExpectedErrors,
    computedMutedErrors,
    fetch,
    fetchPolicy,
    isPrefetchMode,
    observabilityFns,
    options.component,
    options.keepAlive,
    pruningSignal,
    queryProject,
    interoperabilityLayer,
    service,
    boundaryViewToken,
    token,
  ]);
}
